import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import ChevronLeft from 'lucide-react-native/icons/chevron-left';
import ChevronRight from 'lucide-react-native/icons/chevron-right';
import { useRef } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { navHref } from '@/components/riviera/nav-links';
import { EventCard } from '@/components/riviera/event-card';
import { Fonts, Spacing } from '@/constants/theme';
import { useEvents } from '@todo/core/hooks/use-events';
import { useFilters } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { dateHeadingPhrase, getDateRange } from '@todo/core/date-range';
import { useLocale } from '@todo/core/i18n/use-locale';
import { cities } from '@/lib/events-data';
import { EVENTS_INDEX_QUERY, EVENTS_INDEX_SEED_KEY } from '@todo/core/list-queries';
import type { Locale } from '@todo/core/i18n/locales';

import css from './carousel-section.module.css';

/** How many cards a carousel renders — the seeded list holds more than this. */
const CAROUSEL_EVENTS_LIMIT = 10;

type Translate = (key: string, options?: Record<string, unknown>) => string;

function buildHeading(
  activeCity: string,
  activeDate: string,
  customDate: Date | null,
  activeCategory: string,
  t: Translate,
  locale: Locale,
) {
  const cityName = cities.find((c) => c.id === activeCity)?.name;
  const where = activeCity !== 'all' && cityName ? t('common.inCity', { city: cityName }) : '';
  const noun = t(`categoryNouns.${activeCategory === 'all' ? 'all' : activeCategory}`);
  const datePhrase = dateHeadingPhrase(activeDate, customDate, t, locale);
  const when = datePhrase ? ` ${datePhrase}` : '';

  return t('events.heading', { noun, where, when });
}

// Web-only: heading size and the heading↔carousel gap used to come from a JS
// `isLg` check (see events-carousel.tsx) that only resolves after mount,
// shifting the section down ~12px on the static export. Here those are driven
// by carousel-section.module.css media queries, and the horizontal scroller
// is a plain overflow-x div so its gap/padding resolve at first paint too.
export function EventsCarousel() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { activeCity, activeDate, customDate, activeCategory } = useFilters();
  const { from, until } = getDateRange(activeDate, customDate);
  const isDefaultView = activeCity === 'all' && activeDate === 'alltime' && activeCategory === 'all';
  const { events: allEvents, loading, error } = useEvents(
    isDefaultView
      ? EVENTS_INDEX_QUERY
      : {
          city: activeCity === 'all' ? undefined : activeCity,
          category: activeCategory === 'all' ? undefined : activeCategory,
          from,
          until,
        },
    isDefaultView ? EVENTS_INDEX_SEED_KEY : undefined,
  );
  const events = allEvents.slice(0, CAROUSEL_EVENTS_LIMIT);
  const heading = buildHeading(activeCity, activeDate, customDate, activeCategory, t, locale);

  const scrollBy = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <div className={css.section}>
      <div className={css.heading}>
        <h2 className={css.headingText} style={{ color: theme.text, fontFamily: Fonts?.serif }}>
          {heading}
        </h2>
        {/* "See all" was an anchor with no destination. It now points at the
            events hub, which is also how the homepage gets a crawlable link
            into that listing. */}
        {/* style must be a plain object, not the usual `({pressed}) => [...]`
            function: <Link asChild> merges props into this Pressable via
            expo-router's Slot (a thin wrapper around @radix-ui/react-slot),
            whose prop merge spreads style objects together — spreading a
            function copies none of its array contents, so a function style
            here silently loses flexDirection/alignItems/gap and the icon
            wraps onto its own line. Pressed feedback goes through `children`
            instead, which isn't touched by that merge. */}
        <Link href={navHref('/[lang]/events', locale)} asChild>
          <Pressable accessibilityRole="link" style={styles.seeAll}>
            {({ pressed }) => (
              <>
                <Text
                  style={[
                    styles.seeAllText,
                    { color: theme.primary },
                    pressed && { opacity: 0.6 },
                  ]}>
                  {t('common.seeAll')}
                </Text>
                <ArrowRight
                  size={16}
                  color={theme.primary}
                  style={pressed && { opacity: 0.6 }}
                />
              </>
            )}
          </Pressable>
        </Link>
      </div>
      <div className={css.carouselWrap}>
        <div ref={scrollRef} className={css.scroller}>
          {loading ? (
            <Text style={{ color: theme.mutedForeground, paddingHorizontal: Spacing.three }}>
              {t('events.loading')}
            </Text>
          ) : error ? (
            <Text style={{ color: theme.destructive, paddingHorizontal: Spacing.three }}>
              {error}
            </Text>
          ) : events.length === 0 ? (
            <Text style={{ color: theme.mutedForeground, paddingHorizontal: Spacing.three }}>
              {t('events.empty')}
            </Text>
          ) : (
            events.map((event) => <EventCard key={event.id} event={event} />)
          )}
        </div>
        <div className={css.arrows}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('a11y.previous')}
            onPress={() => scrollBy(-1)}
            style={({ pressed }) => [
              styles.navBtn,
              styles.navBtnLeft,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.8 },
            ]}>
            <ChevronLeft size={20} color={theme.text} />
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('a11y.next')}
            onPress={() => scrollBy(1)}
            style={({ pressed }) => [
              styles.navBtn,
              styles.navBtnRight,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.8 },
            ]}>
            <ChevronRight size={20} color={theme.text} />
          </Pressable>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 13,
    fontWeight: '600',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBtnLeft: {
    left: -12,
  },
  navBtnRight: {
    right: -12,
  },
});
